import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { DatePicker, Select } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
    GetReporteAtenciones,
    GetReporteAtencionesExcel,
} from "../../Service/Service.reporteCoberturas";
import { GetTokenDecoded } from "../../utils/storeUsuario";
import { ControllerListarSegurosClinica } from "../../Service/Services.seguros";
import { ListarEspecialidadClinicaActivas } from "../../Service/Service.especialidad";
import { GetExamenesAll } from "../../Service/Service.examen";
import { GetPacientesAll } from "../../Service/Service.paciente";
import { ListadoPlanes } from "../../Service/Service.Plan";
import { ListarDoctorClinica } from "../../Service/Service.doctor";
import DataTable from "../../Components/UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../../Components/UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../../Components/UI/Chip";
import Boton from "../../Components/UI/Boton";
import BotonesExportar from "../../Components/UI/BotonesExportar";
import { useToast } from "../../Components/UI/Aviso";
import { IconoBuscar } from "../../Components/UI/Iconos";
import { usePageSubtitulo } from "../../context/PageHeaderContext";

const { RangePicker } = DatePicker;

// REPORTES-PRODUCCION-FACTURACION-10 -- reporte consolidado por prestación
// (1 fila = 1 prestación raíz). SOLO LECTURA / EXPORTACIÓN. Reutiliza el
// endpoint COBERTURAS-06 extendido (/reporte/coberturas/atenciones), que ya
// devuelve los campos financieros consolidados. Filtros SIEMPRE VISIBLES.
const TIPO_PACIENTE = [
    { value: "PARTICULAR", label: "Particular" },
    { value: "SEGURO_100", label: "Seguro 100%" },
    { value: "COPAGO", label: "Copago" },
    { value: "NO_RESUELTA", label: "No resuelta" },
];
// REPORTES-PRODUCCION-FILTRO-PLAN-QA-11 §21: este reporte sólo contiene
// prestaciones con movimiento financiero reconocido; "Sin distribuir" y
// "Pendiente por procesar" ya no forman parte del universo, así que no se
// ofrecen como filtro.
const ESTADO_SEGURO = [
    { value: "PENDIENTE_FACTURAR", label: "Pendiente de facturar" },
    { value: "FACTURADO", label: "Facturado" },
    { value: "NO_APLICA", label: "No aplica" },
];
// SISTEMA-DE-DISENO: los colores de antd pasan a los tonos del sistema, donde
// el color significa lo mismo en toda la aplicacion. `estado_paciente` y
// `estado_seguro` llegan del backend como TEXTO en mayusculas: se mapean tal
// cual, sin normalizar el dato.
const TONO_ESTADO = {
    FACTURADO: "hecho",
    "NO FACTURADO": "proceso",
    "PENDIENTE DE FACTURAR": "proceso",
    "SIN DISTRIBUIR": "proceso",
    ANULADO: "anulado",
    CANCELADO: "anulado",
    "NO APLICA": "pendiente",
};
const tonoDe = (t) => TONO_ESTADO[String(t || "").toUpperCase()] || "pendiente";

const EMPTY = {
    rangoFechas: null, aseguradora: undefined, plan: undefined, especialidad: undefined,
    medico: undefined, paciente: undefined, examen: undefined,
    tipo_cobertura: undefined, estado_responsabilidad_seguro: undefined,
};
const ZERO = {
    prestaciones: 0, pacientes: 0, valor_servicio: 0,
    paciente_facturado: 0, seguro_pendiente: 0, seguro_facturado: 0, total_facturado: 0,
};

function ReporteProduccionFacturacion() {
    const [draft, setDraft] = useState(EMPTY);
    const [applied, setApplied] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState(ZERO);

    const [seguros, setSeguros] = useState([]);
    const [planes, setPlanes] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [medicos, setMedicos] = useState([]);
    const [examenes, setExamenes] = useState([]);
    const [pacientes, setPacientes] = useState([]);
    const { mostrar } = useToast();

    useEffect(() => {
        (async () => {
            const idclinica = GetTokenDecoded()?.idclinica;
            const seg = await ControllerListarSegurosClinica(idclinica);
            setSeguros((seg || []).map((s) => ({ value: s.idseguro, label: s.nombre })));
            const esp = await ListarEspecialidadClinicaActivas(idclinica);
            const espData = esp?.data || esp || [];
            setEspecialidades((Array.isArray(espData) ? espData : []).map((e) => ({ value: e.idespecialidad, label: e.especialidad })));
            // CONFIG-PLANES-SEGURO-31: reporte histórico -> el filtro de plan debe
            // incluir también planes inactivos (una atención pasada pudo usar uno).
            const pl = await ListadoPlanes({ estado: "Todos" });
            const plData = pl?.data || pl || [];
            setPlanes((Array.isArray(plData) ? plData : []).map((p) => ({ value: p.id_plan, label: p.nombre })));
            const doc = await ListarDoctorClinica(idclinica);
            const docData = doc?.data || doc || [];
            setMedicos((Array.isArray(docData) ? docData : []).map((d) => ({ value: d.iddoctor, label: d.nombre })));
        })();
    }, []);

    const buscarExamenes = async (search) => {
        const r = await GetExamenesAll(1, 15, search || "");
        if (r?.success) setExamenes(r.data.map((d) => ({ value: d.IdExamenTipo, label: `${d.codigo} - ${d.descripcion}` })));
    };
    const buscarPacientes = async (search) => {
        const r = await GetPacientesAll(1, 15, search || "");
        if (r?.success) setPacientes(r.data.map((d) => ({ value: d.idpaciente, label: `${d.cedula} - ${d.nombres_completos}` })));
    };

    const fetchData = useCallback(async (p = 1, ps = 20, filters = {}) => {
        setLoading(true);
        setError(null);
        const res = await GetReporteAtenciones({ page: p, limit: ps, filters });
        if (res?.success) {
            setRows((res.data || []).map((r) => ({ ...r, key: r.idprocedimiento_cita_paciente })));
            setTotal(res.pagination?.total || 0);
            setPage(res.pagination?.page || 1);
            setPageSize(res.pagination?.limit || ps);
            setSummary({ ...ZERO, ...(res.summary || {}) });
        } else {
            // ESTADO DE ERROR: antes la tabla y los totales se vaciaban en
            // silencio, indistinguible de "no hubo produccion en ese rango".
            setRows([]); setTotal(0); setSummary(ZERO);
            setError(res?.message || "No se pudo obtener el reporte de producción / facturación.");
        }
        setLoading(false);
    }, []);

    const buildFilters = () => {
        const [d0, d1] = draft.rangoFechas || [];
        return {
            fecha_inicial: d0 ? d0.format("YYYY-MM-DD") : undefined,
            fecha_final: d1 ? d1.format("YYYY-MM-DD") : undefined,
            aseguradora: draft.aseguradora,
            plan: draft.plan,
            especialidad: draft.especialidad,
            medico: draft.medico,
            paciente: draft.paciente,
            examen: draft.examen,
            tipo_cobertura: draft.tipo_cobertura,
            estado_responsabilidad_seguro: draft.estado_responsabilidad_seguro,
        };
    };

    const aplicar = () => {
        const f = buildFilters();
        setApplied(f);
        fetchData(1, pageSize, f);
    };
    const limpiar = () => {
        setDraft(EMPTY);
        setApplied({});
        fetchData(1, pageSize, {});
    };

    useEffect(() => {
        fetchData(1, pageSize, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
    const fecha = (t) => (t ? format(new Date(t), "dd/MM/yyyy", { locale: es }) : "—");

    usePageSubtitulo(loading ? "" : `${total} prestacion${total === 1 ? "" : "es"}`);

    // Mismo endpoint, mismos filtros y mismo nombre de archivo que antes; lo
    // unico nuevo es que un fallo se avisa en vez de tragarse.
    const exportarExcel = async () => {
        try {
            const r = await GetReporteAtencionesExcel({ filters: applied });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `reporte_produccion_facturacion_${applied.fecha_inicial || "inicio"}_${applied.fecha_final || "fin"}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            mostrar({ tipo: "error", texto: e?.message || "No se pudo generar el Excel." });
        }
    };

    const columnas = [
        {
            // Paciente y fecha de la atención se leen juntos.
            key: "paciente",
            titulo: "Atención",
            render: (r) => (
                <CeldaDoble
                    principal={r.paciente ? tituloDeNombre(r.paciente) : "Sin determinar"}
                    secundario={fecha(r.fecha_emision)}
                />
            ),
        },
        {
            // Servicio con su código; especialidad y motivo, debajo.
            key: "servicio",
            titulo: "Servicio",
            render: (r) => (
                <CeldaDoble
                    principal={r.servicio_codigo ? `${r.servicio_codigo} - ${r.servicio}` : r.servicio}
                    secundario={[r.especialidad, r.motivo].filter(Boolean).join(" · ")}
                />
            ),
        },
        { key: "medico", titulo: "Médico", ancho: 170, render: (r) => <Celda valor={r.medico || "Sin determinar"} /> },
        {
            // Tipo de paciente, y debajo la aseguradora con su plan.
            key: "tipo_cobertura",
            titulo: "Cobertura",
            ancho: 190,
            render: (r) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <ChipCategoria
                        tono={r.tipo_cobertura === "NO_RESUELTA" ? undefined : "info"}
                        incompleto={r.tipo_cobertura === "NO_RESUELTA"}
                    >
                        {TIPO_PACIENTE.find((x) => x.value === r.tipo_cobertura)?.label || r.tipo_cobertura}
                    </ChipCategoria>
                    <span className="dt-doble__secundario">
                        {[r.seguro, r.plan].filter(Boolean).join(" · ") || "Sin aseguradora"}
                    </span>
                </div>
            ),
        },
        {
            // Lo que vale la prestación y cómo se reparte, con el copago.
            key: "valor_servicio",
            titulo: "Valor servicio",
            ancho: 200,
            alinear: "derecha",
            render: (r) => (
                <CeldaDoble
                    principal={money(r.valor_servicio)}
                    secundario={`Pac ${money(r.valor_paciente)}${
                        r.porcentaje_copago == null ? "" : ` (${Number(r.porcentaje_copago).toFixed(0)}%)`
                    } · Seg ${money(r.valor_aseguradora)}`}
                    monoPrincipal
                />
            ),
        },
        {
            // Lo efectivamente facturado: el total manda y debajo el desglose.
            key: "total_facturado",
            titulo: "Facturado",
            ancho: 220,
            alinear: "derecha",
            render: (r) => (
                <CeldaDoble
                    principal={money(r.total_facturado)}
                    secundario={`Pac ${money(r.paciente_facturado)} · Seg ${money(r.seguro_facturado)} · Pend ${money(
                        r.seguro_pendiente
                    )}`}
                    monoPrincipal
                />
            ),
        },
        {
            // Los dos documentos con su fecha, etiquetados: sin etiqueta, dos
            // números sueltos no se distinguen.
            key: "documentos",
            titulo: "Documentos",
            ancho: 200,
            render: (r) => (
                <CeldaDoble
                    principal={r.fac_paciente?.num_documento ? `Pac ${r.fac_paciente.num_documento} · ${fecha(r.fac_paciente.fecha)}` : null}
                    secundario={r.fac_aseguradora?.num_documento ? `Seg ${r.fac_aseguradora.num_documento} · ${fecha(r.fac_aseguradora.fecha)}` : null}
                    monoPrincipal
                    monoSecundario
                />
            ),
        },
        {
            key: "estados",
            titulo: "Estados",
            ancho: 190,
            render: (r) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <ChipEstado tono={tonoDe(r.estado_paciente)}>{r.estado_paciente}</ChipEstado>
                    <ChipEstado tono={tonoDe(r.estado_seguro)}>{r.estado_seguro}</ChipEstado>
                </div>
            ),
        },
    ];

    const hayFiltros = Object.values(applied).some((v) => v !== undefined && v !== null && v !== "");

    const TOTALES = [
        { etiqueta: "Prestaciones", valor: summary.prestaciones },
        { etiqueta: "Pacientes", valor: summary.pacientes },
        { etiqueta: "Valor servicios", valor: money(summary.valor_servicio) },
        { etiqueta: "Facturado paciente", valor: money(summary.paciente_facturado) },
        { etiqueta: "Pendiente seguro", valor: money(summary.seguro_pendiente) },
        { etiqueta: "Facturado seguro", valor: money(summary.seguro_facturado) },
    ];

    return (
        <div className="page-content">
            <div className="container-fluid">
                {/* ---- FILTROS SIEMPRE VISIBLES ---- */}
                <Row className="g-2 align-items-end mb-3 p-2 border rounded">
                    <Col xs={12} md={4} lg={3}>
                        <label className="kc__etiqueta">Rango de fechas</label>
                        <RangePicker className="w-100" value={draft.rangoFechas} onChange={(d) => setDraft((f) => ({ ...f, rangoFechas: d }))} getPopupContainer={() => document.body} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Aseguradora</label>
                        <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todas" options={seguros} value={draft.aseguradora} onChange={(v) => setDraft((f) => ({ ...f, aseguradora: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Plan</label>
                        <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todos" options={planes} value={draft.plan} onChange={(v) => setDraft((f) => ({ ...f, plan: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Especialidad</label>
                        <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todas" options={especialidades} value={draft.especialidad} onChange={(v) => setDraft((f) => ({ ...f, especialidad: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Médico</label>
                        <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todos" options={medicos} value={draft.medico} onChange={(v) => setDraft((f) => ({ ...f, medico: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Paciente</label>
                        <Select className="w-100" allowClear showSearch filterOption={false} placeholder="Buscar" options={pacientes} value={draft.paciente} onSearch={buscarPacientes} onChange={(v) => setDraft((f) => ({ ...f, paciente: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Servicio</label>
                        <Select className="w-100" allowClear showSearch filterOption={false} placeholder="Buscar" options={examenes} value={draft.examen} onSearch={buscarExamenes} onChange={(v) => setDraft((f) => ({ ...f, examen: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Tipo paciente</label>
                        <Select className="w-100" allowClear options={TIPO_PACIENTE} value={draft.tipo_cobertura} onChange={(v) => setDraft((f) => ({ ...f, tipo_cobertura: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Estado seguro</label>
                        <Select className="w-100" allowClear options={ESTADO_SEGURO} value={draft.estado_responsabilidad_seguro} onChange={(v) => setDraft((f) => ({ ...f, estado_responsabilidad_seguro: v }))} />
                    </Col>
                    <Col xs={12} className="d-flex gap-2 mt-2 flex-wrap">
                        <Boton variante="primario" icono={IconoBuscar} onClick={aplicar} data-testid="pf-buscar">
                            Buscar
                        </Boton>
                        <Boton onClick={limpiar} data-testid="pf-limpiar">
                            Limpiar
                        </Boton>
                        {/* Este reporte sólo genera Excel, y lo genera el backend. */}
                        <BotonesExportar
                            permiso="reportes.export"
                            titulo="Producción / Facturación"
                            idPrefijo="pf"
                            formatos={["excel"]}
                            onExcel={exportarExcel}
                        />
                    </Col>
                </Row>

                <div className="ac-filtros-linea mb-2" data-testid="pf-totales">
                    {TOTALES.map((t) => (
                        <div className="ac-panel" key={t.etiqueta} style={{ flex: "1 1 150px", padding: "10px 14px", gap: 2 }}>
                            <span className="ac-etiqueta">{t.etiqueta}</span>
                            <span style={{ fontSize: 17, fontWeight: 600 }}>{t.valor}</span>
                        </div>
                    ))}
                </div>
                <div className="ac-filtros-linea mb-3">
                    <div className="ac-panel" style={{ flex: "0 1 260px", padding: "10px 14px", gap: 2 }}>
                        <span className="ac-etiqueta">Total facturado</span>
                        <span style={{ fontSize: 20, fontWeight: 700 }} data-testid="pf-total-facturado">
                            {money(summary.total_facturado)}
                        </span>
                    </div>
                </div>

                <DataTable
                    columnas={columnas}
                    filas={rows}
                    filaKey="idprocedimiento_cita_paciente"
                    cargando={loading}
                    error={error}
                    onReintentar={() => fetchData(page, pageSize, applied)}
                    etiquetaTabla="Reporte de producción y facturación"
                    hayFiltrosAplicados={hayFiltros}
                    onLimpiarFiltros={limpiar}
                    textoVacioSinFiltros="No hay prestaciones con movimiento financiero todavía."
                    paginacion={{
                        page,
                        pageSize,
                        total,
                        tamanos: [20, 50, 100],
                        onCambio: (p, ps) => fetchData(p, ps, applied),
                    }}
                />
            </div>
        </div>
    );
}

export default ReporteProduccionFacturacion;
